import { usePublicClient, useAccount } from 'wagmi';
import { useState } from 'react';
import { formatEther } from 'viem';

// Import logger correctly
import { logger } from '@/utils/logger';

/**
 * Hook for gas estimation and fee calculation using WalletConnect's infrastructure
 * This replaces custom RPC handling with WalletConnect's built-in providers
 */
export function useWalletConnectGas() {
  const publicClient = usePublicClient();
  const { address } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Estimate gas for a transaction
   * @param to Contract address
   * @param data Encoded function call data
   * @param value Optional value in wei to send with transaction
   * @returns Estimated gas as bigint
   */
  const estimateGas = async (
    to: `0x${string}`, 
    data: `0x${string}`, 
    value?: bigint
  ): Promise<bigint> => {
    setIsLoading(true);
    setError(null);
    
    try {
      logger.info('Estimating gas using WalletConnect infrastructure', { to, dataLength: data.length });
      
      if (!address) {
        throw new Error('No wallet connected');
      }
      
      // This uses WalletConnect's RPC under the hood
      const gasEstimate = await publicClient.estimateGas({
        account: address,
        to,
        data,
        value: value || BigInt(0),
      });
      
      logger.info('Gas estimation result', { gasEstimate: gasEstimate.toString() });
      return gasEstimate;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown gas estimation error');
      logger.error('Gas estimation failed', { error: error.message });
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get current gas price from the network
   * @returns Gas price in wei as bigint
   */
  const getGasPrice = async (): Promise<bigint> => {
    setIsLoading(true);
    setError(null);
    
    try {
      logger.info('Getting gas price using WalletConnect infrastructure');
      const gasPrice = await publicClient.getGasPrice();
      logger.info('Gas price result', { 
        gasPriceWei: gasPrice.toString(),
        gasPriceGwei: formatEther(gasPrice * BigInt(1000000000))
      });
      return gasPrice;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown gas price error');
      logger.error('Gas price fetch failed', { error: error.message });
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Calculate the total transaction fee (gas * gasPrice)
   * @param gasLimit Estimated gas limit
   * @param customGasPrice Optional custom gas price, otherwise fetches current price
   * @returns Total fee in wei as bigint
   */
  const calculateTxFee = async (
    gasLimit: bigint,
    customGasPrice?: bigint
  ): Promise<bigint> => {
    try {
      const gasPrice = customGasPrice || await getGasPrice();
      const txFee = gasPrice * gasLimit;
      
      logger.info('Transaction fee calculated', {
        gasLimit: gasLimit.toString(),
        gasPrice: gasPrice.toString(),
        txFee: txFee.toString(),
        txFeeEth: formatEther(txFee)
      });
      
      return txFee;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Fee calculation error');
      logger.error('Fee calculation failed', { error: error.message });
      throw error;
    }
  };

  /**
   * Format a gas fee for display
   * @param wei Fee in wei
   * @returns Formatted string with ETH value and symbol
   */
  const formatGasFee = (wei: bigint): string => {
    return `${formatEther(wei)} ETH`;
  };

  return { 
    estimateGas, 
    getGasPrice, 
    calculateTxFee, 
    formatGasFee,
    isLoading, 
    error 
  };
}
